import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { fetchSessionProfile, signOut as apiSignOut, supabase } from '@radiology/api-client';
import type { ModuleKey, Role, UserContext } from '@radiology/ui';
import { normalizeRoles } from '../app/roles';
import { SessionContext, type Session } from './SessionContext';

/**
 * The real session, backed by Supabase.
 *
 * Replaces DevSessionProvider from Phase 2. Nothing outside this folder changed
 * when it was swapped in — every consumer uses `useSession()` and never touched
 * the implementation, which was the point of building it that way.
 */

export type SessionState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'must-reset'; email: string }
  | { status: 'ready' };

interface Props {
  children: ReactNode;
  /** Rendered until the first session check settles. */
  fallback?: ReactNode;
  /** Rendered when nobody is signed in. */
  signedOut: (state: SessionState) => ReactNode;
}

export function SessionProvider({ children, fallback, signedOut }: Props) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const [session, setSession] = useState<Session | null>(null);
  const [context, setContextState] = useState<UserContext>({});

  const load = useCallback(async () => {
    const { data } = await supabase().auth.getSession();
    if (!data.session) {
      setSession(null);
      setState({ status: 'signed-out' });
      return;
    }

    const profile = await fetchSessionProfile();
    if (!profile) {
      // Authenticated but with no profile row — a data problem, not an auth one.
      // Signing out silently would hide it, so surface it and stop.
      console.error('signed in but no core.user_profiles row exists for this user');
      await apiSignOut();
      setState({ status: 'signed-out' });
      return;
    }

    // Every migrated user carries this flag until they reset (decision #7).
    // They hold a valid session but must not reach the app.
    if (profile.mustResetPassword) {
      setSession(null);
      setState({ status: 'must-reset', email: data.session.user.email ?? profile.username });
      return;
    }

    const rolesByModule: Partial<Record<ModuleKey, Role[]>> = {};
    for (const [module, roles] of Object.entries(profile.rolesByModule)) {
      rolesByModule[module as ModuleKey] = normalizeRoles(roles ?? []);
    }

    setContextState(prev => ({
      hospital: prev.hospital ?? profile.hospital ?? undefined,
      section: prev.section ?? profile.section ?? undefined,
      role: prev.role,
    }));

    setSession({
      user: {
        username: profile.username,
        displayName: profile.displayName ?? profile.username,
        email: data.session.user.email ?? undefined,
      },
      rolesByModule,
      context: {},
      setActiveRole: () => undefined,
      setContext: () => undefined,
      signOut: () => undefined,
      isAuthenticated: true,
    });
    setState({ status: 'ready' });
  }, []);

  useEffect(() => {
    void load();

    // Keep the app honest if the session dies in another tab or a refresh fails.
    const { data: sub } = supabase().auth.onAuthStateChange(event => {
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setState({ status: 'signed-out' });
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        void load();
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [load]);

  const setActiveRole = useCallback((_module: ModuleKey, role: Role) => {
    setContextState(prev => ({ ...prev, role }));
  }, []);

  const setContext = useCallback((next: Partial<UserContext>) => {
    setContextState(prev => ({ ...prev, ...next }));
  }, []);

  const signOut = useCallback(() => {
    void apiSignOut().then(() => {
      setSession(null);
      setState({ status: 'signed-out' });
    });
  }, []);

  const value = useMemo<Session | null>(
    () => (session ? { ...session, context, setActiveRole, setContext, signOut } : null),
    [session, context, setActiveRole, setContext, signOut],
  );

  if (state.status === 'loading') return <>{fallback ?? null}</>;
  if (!value) return <>{signedOut(state)}</>;

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
